import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { isEmpty } from 'ramda'
import { colors, fontWeights } from '@/styles'
import { isNullOrEmpty } from '@/utils'
import { Label } from '@/components/text'
import { Button } from '@/components/button'
import Tooltip from '@/components/popper-tooltip'
import AddDocumentButton from './add-document-button'
import TrashIcon from './trash-icon.svg'
import RedXIcon from './red-x-icon.svg'
import PencilIcon from './pencil-icon.svg'
import PencilGreyIcon from './pencil-grey-icon.svg'
import DownloadIcon from './download-icon.svg'
import ErrorIcon from './error-icon.svg'
import { showEditNameModal } from './edit-name-modal'

const TableWrapper = styled.div`
  display: block;
  ${props => (props.header ? 'margin-bottom: 12px;' : 'margin-bottom: 38px;')}
  ${props => (props.primary ? 'margin: 16px 0 50px;' : '')}
  ${props => (props.secondary ? 'margin-bottom: 12px;' : '')}
`

const TableLabelWrapper = styled(Label)`
  color: ${colors.grey56};
  margin: 20px 16px 12px;
`

const TableContentWapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding: 24px 16px;
  background: #ffffff;
  box-shadow:
    0px 1px 3px rgba(0, 0, 0, 0.1),
    0px 1px 2px rgba(0, 0, 0, 0.06);
  border-radius: 8px;
`

const TableSubWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
`

const TooltipOuter = styled.div`
  max-width: 250px;
`

const DocumentsIcon = styled.img`
  width: 14px;
  height: 14px;
`

const ContractName = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 16px;
  padding-right: 15px;
  width: fit-content;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
`

const FullText = styled.div`
  font-weight: ${fontWeights.medium};
  font-size: 14px;
  line-height: 16px;
`

const DocumentStatus = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  background: ${props => (props.status === 'error' ? colors.red96 : colors.grey96)};
  border-radius: 16px;
  padding: 0 8px;
  margin-left: 27px;
`

const DocumentStatusText = styled.p`
  font-size: 14px;
  line-height: 16px;
  color: ${props => (props.uploadError ? colors.red24 : colors.grey24)};
  font-weight: ${fontWeights.medium};
  margin: 4px 0 4px 6px;
  max-width: 250px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const TooltipWrapper = styled.div`
  p {
    max-width: 220px;
    margin: 0;
    padding: 0;
  }
`

const Tables = ({
  header,
  primary,
  secondary,
  isMobile,
  contract,
  packageFile = {},
  handleUploadFile,
  handleDelete
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const hasFile = !isEmpty(packageFile)

  const status = {
    pending: t('l.contract_manager.documents.uploading.label', 'Uploading...'),
    in_progress: t('l.contract_manager.documents.uploading.label', 'Uploading...'),
    error: t('l.contract_manager.documents.error.label', 'Error:'),
    canceled: t('l.contract_manager.documents.canceled.label', 'Canceled')
  }

  const editName = () => {
    showEditNameModal({ packageFile, dispatch, contractId: contract?.id })
  }
  const uploadIncomplete = packageFile.upload_status !== 'success'
  const uploadError = packageFile.upload_status === 'error'
  const icon = packageFile.upload_status === 'success' ? TrashIcon : RedXIcon
  const editIcon = uploadIncomplete ? PencilGreyIcon : PencilIcon
  return (
    <TableWrapper header={header && hasFile} primary={primary} secondary={secondary}>
      {!isMobile && !!header && hasFile && (
        <TableLabelWrapper fontSize="14px" type="small">
          <div>{t('l.contract_manager.documents.file.label', 'File')}</div>
        </TableLabelWrapper>
      )}
      {hasFile ? (
        <TableContentWapper>
          <TableSubWrapper>
            <TooltipOuter>
              <Tooltip
                theme="secondary"
                enabled={!isNullOrEmpty(packageFile.file_original_name)}
                size="medium"
                content={
                  <TooltipWrapper>
                    <FullText>{packageFile.file_original_name}</FullText>
                  </TooltipWrapper>
                }
                placement="top"
              >
                <ContractName>{packageFile.file_original_name}</ContractName>
              </Tooltip>
            </TooltipOuter>
            <Button
              theme="text"
              size="small"
              space="none"
              onClick={editName}
              disabled={uploadIncomplete}
            >
              <DocumentsIcon src={editIcon} alt="add" disabled={uploadIncomplete} />
            </Button>
            {uploadIncomplete &&
              (uploadError ? (
                <Tooltip
                  theme="secondary"
                  enabled={true}
                  size="medium"
                  content={
                    <TooltipWrapper>
                      <FullText uploadError={uploadError}>
                        {packageFile.upload_status_reason}
                      </FullText>
                    </TooltipWrapper>
                  }
                  placement={'top'}
                >
                  <>
                    <DocumentStatus status={packageFile.upload_status}>
                      <DocumentsIcon src={ErrorIcon} alt="error" />
                      <DocumentStatusText uploadError={uploadError}>
                        {status?.[packageFile.upload_status]}
                      </DocumentStatusText>
                      <DocumentStatusText uploadError={uploadError}>
                        {packageFile.upload_status_reason}
                      </DocumentStatusText>
                    </DocumentStatus>
                  </>
                </Tooltip>
              ) : (
                <>
                  <DocumentStatus status={packageFile.upload_status}>
                    <DocumentsIcon src={DownloadIcon} alt="downloading" />
                    <DocumentStatusText uploadError={uploadError}>
                      {status?.[packageFile.upload_status]}
                    </DocumentStatusText>
                  </DocumentStatus>
                </>
              ))}
          </TableSubWrapper>
          <Button theme="text" size="small" space="none" onClick={handleDelete}>
            <DocumentsIcon src={icon} alt="delete" />
          </Button>
        </TableContentWapper>
      ) : (
        <AddDocumentButton handleUploadFile={handleUploadFile} isPrimary={primary} />
      )}
    </TableWrapper>
  )
}

export default Tables
